const CONTRACTOR_COST_STATUS = {
  Draft: 1,
  Submitted: 2,
  Approved: 3,
  Rejected: 4,
  Negotiating: 5,
  Superseded: 6,
};

const CUSTOMER_OFFER_STATUS = {
  Draft: 1,
  PendingApproval: 2,
  ApprovedBySolarent: 3,
  RejectedBySolarent: 4,
  Superseded: 5,
  AcceptedByCustomer: 6,
  RejectedByCustomer: 7,
  Canceled: 8,
};

const toStatusNumber = (value, map) => {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  const asNumber = Number(value);
  if (!Number.isNaN(asNumber) && String(value).trim() !== "") {
    return asNumber;
  }
  if (typeof value === "string" && map[value] != null) {
    return map[value];
  }
  return null;
};

export const normalizeContractorCostQuoteStatus = (value) =>
  toStatusNumber(value, CONTRACTOR_COST_STATUS);

export const normalizeCustomerPriceOfferStatus = (value) =>
  toStatusNumber(value, CUSTOMER_OFFER_STATUS);

export const CONTRACTOR_COST_STATUS_CODES = CONTRACTOR_COST_STATUS;
export const CUSTOMER_OFFER_STATUS_CODES = CUSTOMER_OFFER_STATUS;

/** VAT rate applied on top of (contractor cost + platform fee/extras − discount). */
export const CUSTOMER_OFFER_VAT_RATE = 0.1;

const previewCustomerPriceParts = ({
  contractorCost = 0,
  marginPercent = 0,
  marginAmount = "",
  financingCost = 0,
  platformServiceFee = 0,
  warrantyExtensionCost = 0,
  meterCost = 0,
  gridConnectionInstallationCost = 0,
  discountAmount = 0,
}) => {
  const cost = Number(contractorCost) || 0;
  const useAmount = String(marginAmount ?? "").trim().length > 0;
  const margin = useAmount
    ? Number(marginAmount) || 0
    : cost * ((Number(marginPercent) || 0) / 100);
  const meter = Math.max(0, Number(meterCost) || 0);
  const grid = Math.max(0, Number(gridConnectionInstallationCost) || 0);
  const subtotal =
    cost +
    margin +
    (Number(financingCost) || 0) +
    (Number(platformServiceFee) || 0) +
    (Number(warrantyExtensionCost) || 0) +
    meter +
    grid -
    (Number(discountAmount) || 0);
  const taxable = subtotal - meter - grid;
  const vat = taxable > 0 ? Math.round(taxable * CUSTOMER_OFFER_VAT_RATE) : 0;
  return { subtotal, vat };
};

/** Meter / grid-connection costs are not subject to VAT. */
export const previewCustomerOfferVat = (input) => previewCustomerPriceParts(input).vat;

export const previewCustomerPriceFinal = (input) => {
  const { subtotal, vat } = previewCustomerPriceParts(input);
  return Math.max(0, subtotal + vat);
};

/** Default Solarent platform margin % for customer price offers. */
export const DEFAULT_CUSTOMER_OFFER_MARGIN_PERCENT = 12;

/**
 * Platform margin (percent or absolute) is required.
 * Absolute platformServiceFee alone does NOT count.
 */
export const hasCustomerOfferPlatformMargin = ({
  marginPercent,
  marginAmount,
  breakdown,
  customerPriceFinal,
  totalContractorCost,
} = {}) => {
  const amount = Number(marginAmount);
  if (String(marginAmount ?? "").trim() !== "" && amount > 0) {
    return true;
  }
  const pct = Number(marginPercent);
  if (!Number.isNaN(pct) && pct > 0) {
    return true;
  }
  const b = breakdown || {};
  if (Number(b.marginAmount ?? b.MarginAmount) > 0) {
    return true;
  }
  if (Number(b.marginPercent ?? b.MarginPercent) > 0) {
    return true;
  }
  const final = Number(customerPriceFinal);
  const contractor = Number(totalContractorCost);
  if (contractor > 0 && final > contractor) {
    return true;
  }
  return false;
};

export const CUSTOMER_OFFER_MISSING_MARGIN_MESSAGE =
  "اعمال حاشیه/کارمزد سولارنت الزامی است. «درصد حاشیه» (مثلاً ۱۲) یا «مبلغ حاشیه» را وارد کنید. فیلد کارمزد خدمات پلتفرم مبلغ ثابت ریالی است و جایگزین درصد نمی‌شود.";

export const PAYMENT_SCHEDULE_TITLES = {
  loan: "مبلغ وام واریزی",
  remainingFirst: "90 درصد مبلغ آورده متقاضی",
  remainingSecond: "10 درصد مبلغ آورده متقاضی (پس از تحویل پروژه)",
};

/** سهم آورده که تا پس از تحویل پروژه موکول می‌شود. */
export const POST_DELIVERY_CONTRIBUTION_SHARE = 0.1;

const roundRial = (value) => Math.round(Number(value) || 0);

const splitContribution = (remaining) => {
  const total = Math.max(0, roundRial(remaining));
  let postDelivery = roundRial(total * POST_DELIVERY_CONTRIBUTION_SHARE);
  if (postDelivery > total) postDelivery = total;
  return {
    first: total - postDelivery,
    second: postDelivery,
  };
};

/** Default 3-row payment plan: loan + 90% contribution + 10% after delivery. */
export const buildDefaultPaymentSchedule = (finalPrice = 0, suggestedLoanAmount = 0) => {
  const final = Math.max(0, roundRial(finalPrice));
  const loan = Math.min(Math.max(0, roundRial(suggestedLoanAmount)), final);
  const remaining = final - loan;
  const { first, second } = splitContribution(remaining);
  return {
    loanDisbursementAmount: loan,
    remainingFirstHalfAmount: first,
    remainingSecondHalfAmount: second,
  };
};

export const normalizePaymentSchedule = (raw, finalPrice = 0, suggestedLoanAmount = 0) => {
  if (!raw) {
    return buildDefaultPaymentSchedule(finalPrice, suggestedLoanAmount);
  }
  const loanRaw =
    raw.loanDisbursementAmount ??
    raw.LoanDisbursementAmount ??
    raw.loan ??
    0;
  const firstRaw =
    raw.remainingFirstHalfAmount ??
    raw.RemainingFirstHalfAmount ??
    raw.remainingFirst ??
    0;
  const secondRaw =
    raw.remainingSecondHalfAmount ??
    raw.RemainingSecondHalfAmount ??
    raw.remainingSecond ??
    0;

  const final = Math.max(0, roundRial(finalPrice));
  const loan = Math.min(Math.max(0, roundRial(loanRaw)), final);
  const remaining = final - loan;
  let first = Math.max(0, roundRial(firstRaw));
  let second = Math.max(0, roundRial(secondRaw));
  if (Math.abs(first + second - remaining) > 1) {
    const split = splitContribution(remaining);
    first = split.first;
    second = split.second;
  } else {
    second = remaining - first;
    if (second < 0) {
      first = remaining;
      second = 0;
    }
  }
  return {
    loanDisbursementAmount: loan,
    remainingFirstHalfAmount: first,
    remainingSecondHalfAmount: second,
  };
};

export const paymentScheduleToForm = (schedule) => ({
  loan: String(schedule?.loanDisbursementAmount ?? 0),
  remainingFirst: String(schedule?.remainingFirstHalfAmount ?? 0),
  remainingSecond: String(schedule?.remainingSecondHalfAmount ?? 0),
});

export const paymentScheduleFromForm = (form, finalPrice = 0) =>
  normalizePaymentSchedule(
    {
      loanDisbursementAmount: form?.loan,
      remainingFirstHalfAmount: form?.remainingFirst,
      remainingSecondHalfAmount: form?.remainingSecond,
    },
    finalPrice,
    Number(form?.loan) || 0
  );

